// treeLayout.js
//
// 3-PASS GENERATIONSALGORITHMUS:
//   Pass 1: BFS nur über parent-child Kanten (Partner ignoriert)
//   Pass 2: Partner auf gleiche Gen bringen (Gewinner = mehr parent-child Verbindungen)
//   Pass 3: parent-child Konsistenz erzwingen: kind.gen MUSS = parent.gen + 1
//           Partner werden mitgezogen

export const CARD_W     = 140;
export const CARD_H     = 160;
export const H_GAP      = 28;
export const V_GAP      = 70;
export const COUPLE_GAP = 28;

// ─── Hilfsfunktionen ─────────────────────────────────────────────────────────

function slotWidth(slot) {
  return slot.type === 'couple' ? CARD_W * 2 + COUPLE_GAP : CARD_W;
}

function setSlotPos(slot, x, y, positions) {
  if (slot.type === 'couple') {
    positions.set(slot.ids[0], { x, y });
    positions.set(slot.ids[1], { x: x + CARD_W + COUPLE_GAP, y });
  } else {
    positions.set(slot.id, { x, y });
  }
}

function getSlotX(slot, positions) {
  const id = slot.type === 'couple' ? slot.ids[0] : slot.id;
  return positions.get(id)?.x ?? 0;
}

function getSlotY(slot, positions) {
  const id = slot.type === 'couple' ? slot.ids[0] : slot.id;
  return positions.get(id)?.y ?? 0;
}

// ─── 1. GENERATIONEN ─────────────────────────────────────────────────────────

export function assignGenerations(persons, relationships) {
  if (persons.length === 0) return new Map();

  const allIds = persons.map(p => p.id);

  // Indexes
  const pcEdges  = new Map();   // id -> [{neighbor, delta}] — nur parent-child
  const hasParent = new Set();
  const pcCount  = new Map();   // id -> Anzahl parent-child Verbindungen
  const partnerRels = [];       // partner/ex-partner Beziehungen
  const childrenOf  = new Map(); // parentId -> [childId]  (für Pass 3)
  const partnerOf   = new Map(); // id -> [partnerId]       (für Pass 3)

  relationships.forEach(r => {
    if (r.type === 'parent-child') {
      if (!pcEdges.has(r.from)) pcEdges.set(r.from, []);
      if (!pcEdges.has(r.to))   pcEdges.set(r.to,   []);
      pcEdges.get(r.from).push({ neighbor: r.to,   delta: +1 });
      pcEdges.get(r.to  ).push({ neighbor: r.from, delta: -1 });
      hasParent.add(r.to);
      pcCount.set(r.from, (pcCount.get(r.from) || 0) + 1);
      pcCount.set(r.to,   (pcCount.get(r.to)   || 0) + 1);
      if (!childrenOf.has(r.from)) childrenOf.set(r.from, []);
      childrenOf.get(r.from).push(r.to);
    } else if (r.type === 'partner' || r.type === 'ex-partner') {
      partnerRels.push(r);
      if (!partnerOf.has(r.from)) partnerOf.set(r.from, []);
      if (!partnerOf.has(r.to))   partnerOf.set(r.to,   []);
      partnerOf.get(r.from).push(r.to);
      partnerOf.get(r.to).push(r.from);
    }
  });

  // ── Pass 1: BFS nur über parent-child ──────────────────────────────
  const roots   = allIds.filter(id => !hasParent.has(id));
  const seeds   = roots.length > 0 ? roots : [allIds[0]];
  const genMap  = new Map();
  const visited = new Set();
  const queue   = [];

  seeds.forEach(id => {
    if (!visited.has(id)) { visited.add(id); genMap.set(id, 0); queue.push(id); }
  });

  while (queue.length) {
    const id  = queue.shift();
    const gen = genMap.get(id);
    for (const { neighbor, delta } of (pcEdges.get(id) || [])) {
      if (!visited.has(neighbor)) {
        visited.add(neighbor);
        genMap.set(neighbor, gen + delta);
        queue.push(neighbor);
      }
    }
  }
  allIds.forEach(id => { if (!genMap.has(id)) genMap.set(id, 0); });

  // ── Pass 2: Partner angleichen ──────────────────────────────────────
  // Wer mehr parent-child Verbindungen hat, bestimmt die Generation
  let changed = true, safety = 0;
  while (changed && safety++ < 100) {
    changed = false;
    partnerRels.forEach(r => {
      const ga = genMap.get(r.from);
      const gb = genMap.get(r.to);
      if (ga === undefined || gb === undefined || ga === gb) return;
      const ca = pcCount.get(r.from) || 0;
      const cb = pcCount.get(r.to)   || 0;
      const anchor = ca >= cb ? ga : gb;
      genMap.set(r.from, anchor);
      genMap.set(r.to,   anchor);
      changed = true;
    });
  }

  // ── Pass 3: parent-child Konsistenz erzwingen ──────────────────────
  // Nach Pass 2 könnten Eltern verschoben worden sein →
  // Kind.gen MUSS = Eltern.gen + 1 gelten. Falls nicht: Kind + Nachkommen verschieben.
  let changed3 = true; safety = 0;
  while (changed3 && safety++ < 200) {
    changed3 = false;
    relationships.forEach(r => {
      if (r.type !== 'parent-child') return;
      const pg = genMap.get(r.from);
      const cg = genMap.get(r.to);
      if (pg === undefined || cg === undefined) return;
      const expected = pg + 1;
      if (cg === expected) return;

      const diff = expected - cg;

      // BFS: verschiebe das Kind und alle seine Nachkommen + deren Partner
      const toShift = [r.to];
      const shifted = new Set();
      while (toShift.length) {
        const node = toShift.shift();
        if (shifted.has(node)) continue;
        shifted.add(node);
        genMap.set(node, (genMap.get(node) || 0) + diff);
        // Kinder nachziehen
        (childrenOf.get(node) || []).forEach(child => {
          if (!shifted.has(child)) toShift.push(child);
        });
        // Partner auf gleicher Ebene mitziehen
        (partnerOf.get(node) || []).forEach(partner => {
          if (!shifted.has(partner)) {
            genMap.set(partner, (genMap.get(partner) || 0) + diff);
            shifted.add(partner);
            // Kinder des Partners auch nachziehen
            (childrenOf.get(partner) || []).forEach(child => {
              if (!shifted.has(child)) toShift.push(child);
            });
          }
        });
      }
      changed3 = true;
    });
  }

  return genMap;
}

// ─── 2. LAYOUT ───────────────────────────────────────────────────────────────
//
// Slot-Typen:
//   single   : { type:'single', id }
//   extended : { type:'extended', ids:[...exPartners, mainId, currentPartner?] }
//              Reihenfolge: Expartner(n) links → Hauptperson → aktueller Partner rechts
//              Die "Hauptperson" ist die Person mit Eltern in dieser Gen, oder die erste.
//              slotWidth = Anzahl * CARD_W + (Anzahl-1) * COUPLE_GAP
//
// Positionierung: setSlotPos setzt alle ids[i] auf x + i*(CARD_W+COUPLE_GAP)

export function computeLayout(persons, relationships) {
  if (persons.length === 0) return new Map();

  const genMap = assignGenerations(persons, relationships);

  const childrenOf  = new Map();
  const parentsOf   = new Map();
  const currentPartnerOf = new Map(); // id -> [id]  (partner)
  const exPartnerOf      = new Map(); // id -> [id]  (ex-partner)

  relationships.forEach(r => {
    if (r.type === 'parent-child') {
      if (!childrenOf.has(r.from)) childrenOf.set(r.from, []);
      childrenOf.get(r.from).push(r.to);
      if (!parentsOf.has(r.to)) parentsOf.set(r.to, []);
      parentsOf.get(r.to).push(r.from);
    } else if (r.type === 'partner') {
      if (!currentPartnerOf.has(r.from)) currentPartnerOf.set(r.from, []);
      if (!currentPartnerOf.has(r.to))   currentPartnerOf.set(r.to,   []);
      currentPartnerOf.get(r.from).push(r.to);
      currentPartnerOf.get(r.to).push(r.from);
    } else if (r.type === 'ex-partner') {
      if (!exPartnerOf.has(r.from)) exPartnerOf.set(r.from, []);
      if (!exPartnerOf.has(r.to))   exPartnerOf.set(r.to,   []);
      exPartnerOf.get(r.from).push(r.to);
      exPartnerOf.get(r.to).push(r.from);
    }
  });

  const personById = new Map(persons.map(p => [p.id, p]));

  const byGen = new Map();
  persons.forEach(p => {
    const g = genMap.get(p.id);
    if (!byGen.has(g)) byGen.set(g, []);
    byGen.get(g).push(p.id);
  });

  // Sortierung erfolgt NACH der Slot-Erstellung (siehe unten), damit
  // nur die Hauptperson eines Slots das Alter bestimmt, nicht die Partner.

  // Alle Partner-IDs einer Person (current + ex) in derselben Gen
  function allPartnersInGen(id, idSet) {
    return [
      ...(currentPartnerOf.get(id) || []),
      ...(exPartnerOf.get(id) || [])
    ].filter(pid => idSet.has(pid));
  }

  function getSlotsForGen(ids) {
    const idSet  = new Set(ids);
    const placed = new Set();
    const slots  = [];

    ids.forEach(id => {
      if (placed.has(id)) return;

      const allP = allPartnersInGen(id, idSet).filter(p => !placed.has(p));
      if (allP.length === 0) {
        placed.add(id);
        slots.push({ type: 'single', id });
        return;
      }

      // Baue extended slot:
      // Expartner links, id in der Mitte, aktueller Partner rechts
      const exPs      = (exPartnerOf.get(id) || []).filter(p => idSet.has(p) && !placed.has(p));
      const curPs     = (currentPartnerOf.get(id) || []).filter(p => idSet.has(p) && !placed.has(p));
      const slotIds   = [...exPs, id, ...curPs];
      slotIds.forEach(sid => placed.add(sid));
      // Brücken-Slot erkennen: Partner kommt aus einer anderen gen0-Familie
      const mainParentKey = (parentsOf.get(id) || []).slice().sort().join('|');
      let bridgePartnerId = null;
      if (mainParentKey) {
        for (const pid of [...exPs, ...curPs]) {
          const pp = (parentsOf.get(pid) || []).slice().sort().join('|');
          if (pp && pp !== mainParentKey) { bridgePartnerId = pid; break; }
        }
      }
      slots.push({ type: 'extended', ids: slotIds, mainId: id, curPartners: curPs, exPartners: exPs, bridgePartnerId });
    });

    return slots;
  }

  // Breite eines Slots
  function slotW(slot) {
    const n = slot.type === 'single' ? 1 : slot.ids.length;
    return n * CARD_W + (n - 1) * COUPLE_GAP;
  }

  // Setze alle Karten eines Slots auf Position x, y
  function placeSlot(slot, x, y) {
    if (slot.type === 'single') {
      positions.set(slot.id, { x, y });
    } else {
      slot.ids.forEach((sid, i) => {
        positions.set(sid, { x: x + i * (CARD_W + COUPLE_GAP), y });
      });
    }
  }

  // Alle IDs eines Slots
  function slotIds(slot) {
    return slot.type === 'single' ? [slot.id] : slot.ids;
  }

  const sortedGens = [...byGen.keys()].sort((a, b) => a - b);
  const minGen     = sortedGens[0];
  const genY       = g => (g - minGen) * (CARD_H + V_GAP);
  const slotsByGen = new Map();
  sortedGens.forEach(g => {
    const slots = getSlotsForGen(byGen.get(g));

    // Geschwister-Sortierung: nur innerhalb derselben Elterngruppe nach Alter
    // Die Reihenfolge der Gruppen untereinander bleibt unverändert.
    const parentKey = slot => {
      // Brücken-Slot: parentKey des Bridge-Partners (andere Familie) verwenden
      if (slot.bridgePartnerId) {
        const pp = (parentsOf.get(slot.bridgePartnerId) || []).slice().sort().join('|');
        if (pp) return pp;
      }
      const mainId = slot.type === 'single' ? slot.id : slot.mainId;
      let parents = (parentsOf.get(mainId) || []).slice().sort().join('|');
      // Falls mainId keine Eltern hat, prüfe ob ein Partner Eltern hat
      if (!parents && slot.type !== 'single') {
        for (const pid of slot.ids) {
          if (pid === mainId) continue;
          const pp = (parentsOf.get(pid) || []).slice().sort().join('|');
          if (pp) { parents = pp; break; }
        }
      }
      return parents || `__no_parent__${mainId}`;
    };

    // Gruppiere Slots (Reihenfolge der Gruppen beibehalten)
    const groups = new Map();
    const groupOrder = [];
    slots.forEach(slot => {
      const key = parentKey(slot);
      if (!groups.has(key)) { groups.set(key, []); groupOrder.push(key); }
      groups.get(key).push(slot);
    });

    // Innerhalb jeder Geschwistergruppe nach Geburtsdatum sortieren.
    // Brücken-Slots: Geburtsdatum des Bridge-Partners verwenden (nicht mainId),
    // damit z.B. [Erika|Andreas] nach Andreas' Alter in der Annerose+Wolfgang-Gruppe einsortiert wird.
    groups.forEach(group => {
      group.sort((sA, sB) => {
        const idA = sA.bridgePartnerId || (sA.type === 'single' ? sA.id : sA.mainId);
        const idB = sB.bridgePartnerId || (sB.type === 'single' ? sB.id : sB.mainId);
        const da  = personById.get(idA)?.birthDate || '';
        const db  = personById.get(idB)?.birthDate || '';
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        return da < db ? -1 : da > db ? 1 : 0;
      });
    });

    // Gruppen in ursprünglicher Reihenfolge zusammensetzen
    const sortedSlots = groupOrder.flatMap(key => groups.get(key));
    slotsByGen.set(g, sortedSlots);
  });

  const positions = new Map();

  // ── Positionierung über Teilbaum-Breiten ─────────────────────────────
  // Jeder Slot bekommt ein horizontales Intervall, das breit genug für ihn
  // selbst UND den ganzen Block seiner Nachkommen ist. Kinder stehen so immer
  // direkt unter ihren Eltern, Geschwistergruppen überlappen nie, und Eltern
  // rücken automatisch auseinander, wenn ihre Kinder mehr Platz brauchen.

  const slotOf    = new Map(); // personId -> slot
  const genOfSlot = new Map(); // slot -> gen
  slotsByGen.forEach((slots, g) => slots.forEach(s => {
    genOfSlot.set(s, g);
    slotIds(s).forEach(id => slotOf.set(id, s));
  }));

  // Person, die bestimmt, zu welchen Eltern ein Slot gehört
  const anchorIdOf = s => s.type === 'single' ? s.id : (s.bridgePartnerId || s.mainId);

  // Eltern-Slot jedes Slots bestimmen (muss genau eine Generation höher liegen)
  const kidsOfSlot   = new Map(); // parentSlot -> [kidSlot]
  const parentSlotOf = new Map(); // kidSlot -> parentSlot
  const kidParentIds = new Map(); // kidSlot -> [parentIds]
  sortedGens.forEach(g => {
    slotsByGen.get(g).forEach(s => {
      let pIds = parentsOf.get(anchorIdOf(s)) || [];
      if (!pIds.length && s.type !== 'single') {
        for (const id of s.ids) {
          const pp = parentsOf.get(id) || [];
          if (pp.length) { pIds = pp; break; }
        }
      }
      const cand = pIds.map(pid => slotOf.get(pid)).filter(ps => ps && genOfSlot.get(ps) === g - 1);
      if (!cand.length) return;
      // Slot, der die meisten Elternteile enthält
      const ps = cand.slice().sort((a, b) =>
        cand.filter(x => x === b).length - cand.filter(x => x === a).length)[0];
      parentSlotOf.set(s, ps);
      kidParentIds.set(s, pIds);
      if (!kidsOfSlot.has(ps)) kidsOfSlot.set(ps, []);
      kidsOfSlot.get(ps).push(s);
    });
  });

  // X-Versatz (relativ zum linken Slot-Rand), unter dem die Kinder eines
  // Elternpaares hängen – identisch zur Logik der Verbindungslinien.
  function cardOffset(s, id) {
    const i = s.type === 'single' ? 0 : s.ids.indexOf(id);
    return i * (CARD_W + COUPLE_GAP);
  }
  function pairAnchorOffset(s, pIds) {
    const inSlot = pIds.filter(pid => slotIds(s).includes(pid));
    if (inSlot.length === 0) return slotW(s) / 2;
    if (inSlot.length === 1) return cardOffset(s, inSlot[0]) + CARD_W / 2;
    const [a, b] = inSlot;
    const xa = cardOffset(s, a), xb = cardOffset(s, b);
    const left = Math.min(xa, xb), right = Math.max(xa, xb);
    const isEx = (exPartnerOf.get(a) || []).includes(b);
    if (isEx) {
      // Kind unter dem Expartner (nicht der Hauptperson des Slots)
      const exId = a === s.mainId ? b : a;
      return cardOffset(s, exId) + CARD_W / 2;
    }
    return (left + CARD_W + right) / 2;
  }

  // Kinder eines Slots nach Elternpaar gruppieren (Paare von links nach rechts),
  // innerhalb eines Paares bleibt die Altersreihenfolge erhalten.
  kidsOfSlot.forEach((kids, ps) => {
    const off = k => pairAnchorOffset(ps, kidParentIds.get(k) || []);
    const withIdx = kids.map((k, i) => ({ k, i, o: off(k) }));
    withIdx.sort((A, B) => (A.o - B.o) || (A.i - B.i));
    kidsOfSlot.set(ps, withIdx.map(x => x.k));
  });

  function anchorOffset(s) {
    const kids = kidsOfSlot.get(s) || [];
    if (!kids.length) return slotW(s) / 2;
    const offs = kids.map(k => pairAnchorOffset(s, kidParentIds.get(k) || []));
    return (Math.min(...offs) + Math.max(...offs)) / 2;
  }

  // Ausdehnung eines Teilbaums links/rechts vom Anker-Punkt
  const extMemo = new Map();
  function kidsBlockW(s) {
    const kids = kidsOfSlot.get(s) || [];
    if (!kids.length) return 0;
    return kids.reduce((sum, k) => sum + subW(k), 0) + H_GAP * (kids.length - 1);
  }
  function ext(s) {
    if (extMemo.has(s)) return extMemo.get(s);
    const off = anchorOffset(s);
    const half = kidsBlockW(s) / 2;
    const e = { left: Math.max(off, half), right: Math.max(slotW(s) - off, half) };
    extMemo.set(s, e);
    return e;
  }
  function subW(s) { const e = ext(s); return e.left + e.right; }

  function placeSubtree(s, left) {
    const e = ext(s);
    const anchorX = left + e.left;
    placeSlot(s, anchorX - anchorOffset(s), genY(genOfSlot.get(s)));
    const kids = kidsOfSlot.get(s) || [];
    let x = anchorX - kidsBlockW(s) / 2;
    kids.forEach(k => { placeSubtree(k, x); x += subW(k) + H_GAP; });
  }

  function depthOf(s) {
    const kids = kidsOfSlot.get(s) || [];
    return 1 + (kids.length ? Math.max(...kids.map(depthOf)) : 0);
  }

  // Wurzeln: oberste Generation + Slots ohne Eltern-Slot (z.B. ohne erfasste Eltern)
  const roots = [];
  sortedGens.forEach(g => slotsByGen.get(g).forEach(s => { if (!parentSlotOf.has(s)) roots.push(s); }));

  const cursor = new Map(); // gen -> rechter Rand
  roots.forEach(r => {
    const g0 = genOfSlot.get(r);
    const d  = depthOf(r);
    let left = null;
    for (let g = g0; g < g0 + d; g++) {
      if (cursor.has(g)) left = Math.max(left ?? -Infinity, cursor.get(g) + H_GAP);
    }
    if (left === null) left = 0;
    placeSubtree(r, left);
    const right = left + subW(r);
    for (let g = g0; g < g0 + d; g++) cursor.set(g, Math.max(cursor.get(g) ?? -Infinity, right));
  });

  // Ganzen Baum um x = 0 zentrieren
  const xs = [...positions.values()].map(p => p.x);
  if (xs.length) {
    const shift = -(Math.min(...xs) + Math.max(...xs) + CARD_W) / 2;
    positions.forEach(p => { p.x += shift; });
  }

  return positions;
}

function resolveOverlaps(slots, positions, y) {
  for (let iter = 0; iter < 40; iter++) {
    let moved = false;
    for (let i = 0; i < slots.length - 1; i++) {
      const sA = slots[i], sB = slots[i + 1];
      const xA = getSlotX(sA, positions);
      const xB = getSlotX(sB, positions);
      if (xA === 0 && xB === 0) continue; // not yet placed
      const overlap = xA + slotWidth(sA) + H_GAP - xB;
      if (overlap > 0.5) {
        setSlotPos(sA, xA - overlap / 2, y, positions);
        setSlotPos(sB, xB + overlap / 2, y, positions);
        moved = true;
      }
    }
    if (!moved) break;
  }
}

// ─── 3. VERBINDUNGSLINIEN ────────────────────────────────────────────────────

export function computeLines(relationships, positions) {
  const lines = [];
  const OVERLAP = 6;

  // Partner-Linien (inkl. Expartner gestrichelt)
  // Expartner-Linie geht durch alle dazwischenliegenden Karten hindurch
  const drawnPairs = new Set();
  relationships.forEach(r => {
    if (r.type !== 'partner' && r.type !== 'ex-partner') return;
    const key = [r.from, r.to].sort().join('|');
    if (drawnPairs.has(key)) return;
    drawnPairs.add(key);
    const a = positions.get(r.from);
    const b = positions.get(r.to);
    if (!a || !b) return;
    const left  = a.x <= b.x ? a : b;
    const right = a.x <= b.x ? b : a;
    const y     = left.y + CARD_H / 2;
    lines.push({
      type: r.type,
      dash: r.type === 'ex-partner' ? '6,4' : undefined,
      // Linie mit Überlappung in beide Karten, geht ggf. durch Karten dazwischen
      path: `M ${left.x + CARD_W - OVERLAP} ${y} L ${right.x + OVERLAP} ${y}`
    });
  });

  // Eltern-Kind-Linien
  // Anker-X = Mittelpunkt zwischen den zwei Eltern (nicht Durchschnitt aller)
  // Bei single parent: Mitte dieser Person
  const groups = new Map();
  relationships.forEach(r => {
    if (r.type !== 'parent-child') return;
    if (!positions.has(r.from) || !positions.has(r.to)) return;
    const parentKey = relationships
      .filter(r2 => r2.type === 'parent-child' && r2.to === r.to && positions.has(r2.from))
      .map(r2 => r2.from).sort().join('|');
    if (!groups.has(parentKey)) groups.set(parentKey, new Set());
    groups.get(parentKey).add(r.to);
  });

  const bars = [];
  groups.forEach((childIds, parentKey) => {
    const pIds = parentKey.split('|').filter(Boolean);
    const pPos = pIds.map(id => positions.get(id)).filter(Boolean);
    if (!pPos.length) return;
    const cIds = [...childIds];
    const cPos = cIds.map(id => positions.get(id)).filter(Boolean);
    if (!cPos.length) return;

    // Anker-X: für Expartner-Kinder → unter dem Expartner; für aktuelle Partner → Mitte der Lücke
    let parentCX;
    if (pPos.length === 1) {
      parentCX = pPos[0].x + CARD_W / 2;
    } else {
      const left   = pPos[0].x <= pPos[1].x ? pPos[0] : pPos[1];
      const right  = pPos[0].x <= pPos[1].x ? pPos[1] : pPos[0];
      const leftId  = pIds[pPos[0].x <= pPos[1].x ? 0 : 1];
      const rightId = pIds[pPos[0].x <= pPos[1].x ? 1 : 0];
      // Prüfe ob es Expartner sind
      const isEx = relationships.some(r =>
        r.type === 'ex-partner' &&
        ((r.from === leftId && r.to === rightId) || (r.from === rightId && r.to === leftId))
      );
      if (isEx) {
        // Kind direkt unter dem Expartner (nicht Mitte)
        const leftIsEx  = relationships.some(r => r.type==='ex-partner' && ((r.from===leftId&&r.to===rightId)||(r.from===rightId&&r.to===leftId)));
        // Finde welcher der beiden der "Expartner" ist (nicht die Hauptperson)
        // Der Expartner ist der, der NICHT currentPartner der anderen Person ist
        const leftIsCurrent  = relationships.some(r => r.type==='partner' && ((r.from===leftId&&r.to===rightId)||(r.from===rightId&&r.to===leftId)));
        if (!leftIsCurrent) {
          parentCX = left.x + CARD_W / 2;  // linker ist der Expartner
        } else {
          parentCX = right.x + CARD_W / 2; // rechter ist der Expartner
        }
      } else {
        parentCX = (left.x + CARD_W + right.x) / 2;
      }
    }

    // Linie startet bei der vertikalen Mitte der Eltern (= Partner-Linie)
    const cXs = cPos.map(p => p.x + CARD_W / 2);
    bars.push({
      parentCX,
      parentMidY:    pPos[0].y + CARD_H / 2,
      parentBottomY: pPos[0].y + CARD_H,
      childTopY:     Math.min(...cPos.map(p => p.y)),
      cIds,
      hMinX: Math.min(parentCX, ...cXs),
      hMaxX: Math.max(parentCX, ...cXs),
    });
  });

  // ── Waagerechte Linien auf "Spuren" verteilen ─────────────────────────
  // Überlappen sich zwei waagerechte Linien zwischen denselben Generationen,
  // bekommen sie unterschiedliche Höhen, damit sie sich nie berühren.
  const LANE_GAP = 14; // Mindestabstand in x zwischen Linien auf derselben Spur
  const byLevel = new Map();
  bars.forEach(b => {
    const key = Math.round(b.parentBottomY);
    if (!byLevel.has(key)) byLevel.set(key, []);
    byLevel.get(key).push(b);
  });
  byLevel.forEach(levelBars => {
    levelBars.sort((a, b) => a.hMinX - b.hMinX);
    const laneEnds = [];
    levelBars.forEach(b => {
      let lane = laneEnds.findIndex(end => b.hMinX > end + LANE_GAP);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(b.hMaxX); }
      else laneEnds[lane] = b.hMaxX;
      b.lane = lane;
    });
    const nLanes = laneEnds.length;
    levelBars.forEach(b => {
      const gap = b.childTopY - b.parentBottomY;
      b.midY = b.parentBottomY + gap * (b.lane + 1) / (nLanes + 1);
    });
  });

  bars.forEach(b => {
    // Senkrecht von Eltern-Mitte nach midY
    lines.push({ type: 'connector', path: `M ${b.parentCX} ${b.parentMidY} L ${b.parentCX} ${b.midY}` });
    // Waagerechte Linie bei midY
    if (b.hMaxX - b.hMinX > 1) {
      lines.push({ type: 'connector', path: `M ${b.hMinX} ${b.midY} L ${b.hMaxX} ${b.midY}` });
    }
    // Senkrecht von midY zu jedem Kind
    b.cIds.forEach(cid => {
      const cp = positions.get(cid);
      if (!cp) return;
      lines.push({ type: 'connector', path: `M ${cp.x + CARD_W / 2} ${b.midY} L ${cp.x + CARD_W / 2} ${cp.y}` });
    });
  });

  return lines;
}
